"use client";
import {
  ButtonComponent,
  LoaderComponent,
  ModalComponent,
  PaperComponent,
} from "@/components";
import {
  APP_KEYS,
  CurrencyConverter,
  GET_PRODUCT_APPLICATION,
  IMAGES,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  SUBSTAGE,
  toTitleCase,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import {
  useCreateLegalityMutation,
  useGetApplicationQuery,
  useGetCustomerQuery,
  useGetESignQuery,
  useGetInsuranceQuery,
  useUpdateInsuranceMutation,
  useLazyGetlegalityStatusQuery,
} from "@/lib/redux";
import { setSelectedPlans } from "@/lib/redux/slice/InsuranceSlice";
import InsurancePlan from "@/screens/protection-plans";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import NomineeFields from "./insurance-banner/insurance-modal/insurance-nominee";
import Image from "next/image";
import { mask, sessionStorageUtil } from "@/lib/utils/common.utils";
import { checkForRpl, checkForRplProductRoute } from "@/lib/utils/rpl.utils";

const EsignScreen = ({ product }) => {
  const t = useTranslations("esign");
  const router = useRouter();
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const { setActions } = useFooter();
  const searchParams = useSearchParams();
  const [showLoading, setShowLoading] = useState(false);
  const [dobMismatchModalVisible, setDobMismatchModalVisible] = useState(false);
  const [nomineeDetailsModalVisible, setNomineeDetailsModalVisible] =
    useState(false);
  const [offerReminder, setOfferReminder] = useState(false);
  const [fullView, setFullView] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAgreementDocumentModalOpen, setIsAgreementDocumentModalOpen] =
    useState(false);

  const { insuranceDeclaration, insuranceSelected, selectedPlans, totalPlans } =
    useSelector((s) => s.insurance);
  const documentId = searchParams.get("documentId");
  /***************************************RTK************************************ */
  const { data: customerData } = useGetCustomerQuery();
  const {
    data: applicationData,
    error: errorGetApplication,
    isLoading: applicationLoading,
    refetch: refetchApplicationData,
  } = useGetApplicationQuery("", { refetchOnMountOrArgChange: true });
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );

  const [
    triggerLegalityStatus,
    {
      data: legalityStatusData,
      error: legalityStatusError,
      isFetching: legalityStatusLoading,
      isSuccess: isLegalityStatusSuccess,
      isError: isLegalityStatusError,
    },
  ] = useLazyGetlegalityStatusQuery();

  const {
    data: eSignData,
    error: errorESign,
    isLoading: eSignLoading,
    refetch: refetchESignData,
  } = useGetESignQuery(
    { appId: productApplication?.appId },
    {
      skip: !productApplication?.appId,
      refetchOnMountOrArgChange: true,
    },
  );

  //add nominee details post api call here
  const [
    createLegality,
    {
      data: creatLegalityData,
      isLoading: createLegalityLoading,
      error: errorlegalityDataApi,
      isSuccess: isSuccessLegality,
    },
  ] = useCreateLegalityMutation();

  const [
    insuranceUpdate,
    {
      data: insuranceUpdateData,
      isLoading: loadingInsuranceUpdateData,
      error: insuranceUpdateDataError,
    },
  ] = useUpdateInsuranceMutation();

  const isError =
    errorESign ||
    errorGetApplication ||
    errorlegalityDataApi ||
    isLegalityStatusError ||
    legalityStatusError;

  /***************************************EFFECT************************************ */

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const handleBackButton = () => {
      router.replace("/");
    };

    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [router]);

  useEffect(() => {
    if (insuranceUpdateData) {
      refetchESignData();
    }
  }, [insuranceUpdateData]);

  useEffect(() => {
    const showNomineeModal = sessionStorage.getItem("nominee_modal") === "true";

    setNomineeDetailsModalVisible(showNomineeModal);
  }, []);

  useErrorHandling(isError);

  useEffect(() => {
    setIsLoading(
      eSignLoading ||
        createLegalityLoading ||
        applicationLoading ||
        legalityStatusLoading,
    );
  }, [
    eSignLoading,
    createLegalityLoading,
    applicationLoading,
    legalityStatusLoading,
  ]);

  useEffect(() => {
    const isRPL = checkForRplProductRoute(product);
    const redirectAfterDigikyc = Boolean(documentId); // has documentId implies redirected, hence don't trigger it again;
    const correctProductType =
      sessionStorageUtil.get(APP_KEYS.PRODUCT_TYPE) === product;
    const shouldSendEvent = redirectAfterDigikyc ? correctProductType : true; // if redirected, send event only if product type is correct;

    sendEventAnalytics({
      eventName: isRPL
        ? WEB_EVENTS.RL_ON_INSURANCE_SCREEN
        : WEB_EVENTS.ON_INSURANCE_SCREEN,
      skip: !shouldSendEvent,
      ...productApplication,
      ...customerData?.data,
    });
  }, [documentId]);

  useEffect(() => {
    if (documentId) {
      const sessionProduct = sessionStorageUtil.get(APP_KEYS.PRODUCT_TYPE);
      if (sessionProduct && sessionProduct !== product) {
        // handling RPL routing after legality
        router.replace(`/${sessionProduct}/e-sign?documentId=${documentId}`);
        return;
      }
    }
    if (
      productApplication?.stage &&
      (productApplication?.stage !== STAGE.E_SIGN ||
        productApplication?.stage !== STAGE.E_SIGN_KFS_INCOMPLETE)
    ) {
      router.replace(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [productApplication?.stage, documentId, product]);

  useEffect(() => {
    if (errorESign) {
      refetchESignData();
    }
  }, [eSignData]);

  useEffect(() => {
    if (
      productApplication?.appId &&
      (eSignData?.data?.insuranceTaken ||
        productApplication?.agreementDocumentId !== null ||
        productApplication?.kfsDocumentId !== null)
    ) {
      setFullView(true);
    }
  }, [eSignData?.data, productApplication]);

  useEffect(() => {
    if (isSuccessLegality) {
      if (
        creatLegalityData?.data?.reason ===
        SUBSTAGE.UCIC_HUNTER_DECISION_POSTPONED_REJECT
      ) {
        refetchApplicationData();
        return;
      }
      if (creatLegalityData?.data?.signUrl) {
        setShowLoading(true);
        product && sessionStorage.setItem(APP_KEYS.PRODUCT_TYPE, product);
        window.location.href = `${creatLegalityData?.data?.signUrl}`;
      } else if (
        [
          SUBSTAGE.PAN_AADHAR_MISMATCH,
          SUBSTAGE.PAN_AADHAR_MATCH_DEFERRED,
          SUBSTAGE.PAN_DOB_MATCH_DEFERRED,
          SUBSTAGE.PAN_DOB_MISMATCH,
        ].includes(creatLegalityData?.data?.subStage)
      ) {
        setShowLoading(false);
        setDobMismatchModalVisible(true);
      }
    }
  }, [isSuccessLegality, product]);

  useEffect(() => {
    setActions({
      showConfirm: true,
      showReject: true,
      disabledConfirmBtn: !eSignData?.data?.insuranceTaken
        ? insuranceSelected && !insuranceDeclaration
        : false,
      onConfirm:
        eSignData?.data?.insuranceTaken ||
        productApplication?.nomineeName ||
        productApplication?.agreementDocumentId ||
        productApplication?.kfsDocumentId ||
        (!eSignData?.data?.insuranceTaken && insuranceSelected)
          ? handleProceed
          : handleLimitedOfferModal,
    });
  }, [
    setActions,
    productApplication,
    insuranceSelected,
    eSignData?.data?.insuranceTaken,
    productApplication?.agreementDocumentId,
    productApplication?.kfsDocumentId,
    productApplication?.nomineeName,
    insuranceDeclaration,
  ]);

  useEffect(() => {
    if (legalityStatusData?.data?.stage) {
      router.replace(STAGE_BASED_ROUTING(legalityStatusData?.data?.stage));
    } else if (
      legalityStatusData?.data?.message ||
      legalityStatusData?.data?.url
    ) {
      setIsAgreementDocumentModalOpen(true);
    }
  }, [legalityStatusData]);

  useEffect(() => {
    if (
      productApplication?.appId &&
      productApplication?.kfsDocumentStatus === "Completed" &&
      productApplication?.agreementDocumentStatus === null
    ) {
      handleLegalityStatus();
    }
  }, [productApplication]);

  /***************************************ACTION************************************ */
  const handleLimitedOfferModal = () => {
    setOfferReminder((o) => !o);
  };

  const handleNomineeAPiRequest = () => {
    setNomineeDetailsModalVisible(false);
    sessionStorage.setItem("nominee_modal", false);
    makeLegalityCall();
  };

  const setNomineeModal = (value) => {
    sessionStorage.setItem("nominee_modal", String(value));
    setNomineeDetailsModalVisible(value);
  };

  const handleNominee = () => {
    setNomineeDetailsModalVisible(false);
    sessionStorage.setItem("nominee_modal", "false");
  };

  const makeLegalityCall = () => {
    sendEventAnalytics({
      eventName: checkForRpl(productApplication)
        ? WEB_EVENTS.RL_E_SIGN_STARTED
        : WEB_EVENTS.E_SIGN_STARTED,
      ...productApplication,
      ...customerData?.data,
      sanctionedAmount: eSignData?.data?.loanAmount,
    });
    // cleaning mandateId if any exists, since mandate is done
    if (sessionStorageUtil?.has(APP_KEYS.MANDATE_ID)) {
      sessionStorageUtil?.remove(APP_KEYS.MANDATE_ID);
    }
    createLegality({
      appId: productApplication?.appId,
    });
  };
  const handleDobMismatchError = () => {
    setDobMismatchModalVisible(false);
    if (
      creatLegalityData?.data?.subStage === SUBSTAGE?.PAN_DOB_MATCH_DEFERRED
    ) {
      router.push(`/${product}/e-sign`);
    } else if (
      creatLegalityData?.data?.subStage === SUBSTAGE?.PAN_DOB_MISMATCH
    ) {
      router.push(`/${product}/offers`);
    }
  };
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const DetailsField = (header, value, subText) => {
    return (
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography
          fontWeight={400}
          color={WEB_COLORS.mainColorShade2}
          sx={{ fontSize: { xs: "12px", md: "16px" } }}
        >
          {header}
        </Typography>
        <Stack>
          <Typography
            fontWeight={500}
            sx={{ fontSize: { xs: "12px", md: "16px" } }}
            color={WEB_COLORS.darkTeal}
            textAlign={"right"}
          >
            {value}
          </Typography>
          {subText && (
            <Typography
              fontWeight={500}
              sx={{ fontSize: { xs: "10px", md: "12px" } }}
              color={WEB_COLORS.darkTeal}
              textAlign={"right"}
            >
              {subText}
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  };

  const handleLegalityStatus = () => {
    if (!productApplication?.appId) return;
    triggerLegalityStatus(productApplication.appId);
  };

  const handleProceed = async () => {
    sendEventAnalytics({
      eventName: WEB_EVENTS.INSURANCE_OPTED_FINAL,
      ...productApplication,
      ...customerData?.data,
    });

    if (
      productApplication?.kfsDocumentStatus === "Completed" &&
      productApplication?.agreementDocumentStatus === null
    ) {
      handleLegalityStatus();
      return;
    }

    if (offerReminder) {
      let n = totalPlans;
      const newSelectedPlans = Array.from({ length: n }, (_, i) => i + 1);
      const insurancePayload = {
        appId: productApplication.appId,
        selectedPlan: newSelectedPlans,
        selectOfferRequest: {
          offerId: 0,
          newRequestLoanAmount: 0,
          selectedTenure: 0,
        },
        insuranceConsent: false,
        isConsentRequest: false,
      };
      dispatch(setSelectedPlans(newSelectedPlans));
      try {
        await insuranceUpdate(insurancePayload).unwrap();
      } catch (err) {
        console.error(err);
      } finally {
        setOfferReminder(false);
      }
    } else {
      if (!eSignData?.data?.insuranceTaken && insuranceSelected !== "") {
        const insurancePayload = {
          appId: productApplication.appId,
          selectedPlan: selectedPlans,
          selectOfferRequest: {
            offerId: 0,
            newRequestLoanAmount: 0,
            selectedTenure: 0,
          },
          insuranceConsent: true,
          isConsentRequest: true,
        };
        try {
          await insuranceUpdate(insurancePayload).unwrap();
        } catch (err) {
          console.error(err);
        }
      }
    }

    if (productApplication?.stage === STAGE.E_SIGN) {
      if (
        ((productApplication?.agreementDocumentId ||
          productApplication?.kfsDocumentId) &&
          !productApplication?.nomineeName) ||
        productApplication?.nomineeName
      ) {
        makeLegalityCall();
        return;
      }
      if (
        !productApplication?.nomineeName &&
        !productApplication?.agreementDocumentId &&
        !productApplication?.kfsDocumentId
      ) {
        setNomineeDetailsModalVisible(true);
        setNomineeModal(true);
      } else {
        makeLegalityCall();
      }
    } else {
      router.push("/");
    }
  };

  const handleContinueWithoutInsurance = async () => {
    if (offerReminder) {
      setOfferReminder(false);
    }
    sendEventAnalytics({
      eventName: WEB_EVENTS.INSURANCE_SKIPPED_FINAL,
      ...productApplication,
      ...customerData?.data,
    });
    const insurancePayload = {
      appId: productApplication.appId,
      insuranceConsent: false,
      isConsentRequest: true,
    };
    await insuranceUpdate(insurancePayload).unwrap();

    if (productApplication?.stage === STAGE.E_SIGN) {
      makeLegalityCall();
      return;
    } else {
      router.push("/");
    }
  };
  /***************************************RENDER************************************ */
  return (
    <Stack>
      <LoaderComponent open={isLoading || showLoading} />
      <PaperComponent
        noDrop
        paperStyle={{
          backgroundColor: "#F2F7F733",
          p: { xs: 1, md: 2 },
          border: "1px solid #F2F7F7",
        }}
      >
        <Stack>
          <Typography
            color={"#004135"}
            fontWeight={700}
            sx={{ fontSize: { xs: "20px", md: "32px" } }}
          >
            Loan Sanction Summary
          </Typography>
          <PaperComponent noDrop paperStyle={{ backgroundColor: "#E5F1F1" }}>
            <Stack gap={{ xs: 2, md: 4 }}>
              {DetailsField(
                t("loanAmount"),
                CurrencyConverter(eSignData?.data?.loanAmount ?? 0),
              )}
              {DetailsField(
                t("loanApplication"),
                eSignData?.data?.loanApplicationNumber,
              )}
              {DetailsField(
                "Processing Fee 1.5% (18% GST Incl.)",
                CurrencyConverter(eSignData?.data?.processingFees ?? 0),
              )}
              {!!eSignData?.data?.techDocumentationFees &&
                DetailsField(
                  "Tech/Documentation Fee - 1% (18% GST Incl.)",
                  CurrencyConverter(
                    eSignData?.data?.techDocumentationFees ?? 0,
                  ),
                )}
              {DetailsField(
                t("disbursementAmount"),
                `${CurrencyConverter(eSignData?.data?.disburmentAmount ?? 0)}`,
                `*(Tentative)`,
              )}
              {fullView && (
                <>
                  {DetailsField(
                    t("emi"),
                    CurrencyConverter(eSignData?.data?.EMI ?? 0),
                  )}
                  {DetailsField(
                    "Annual Percentage Rate*",
                    `${eSignData?.data?.annualPercentageRate}%`,
                    `*(Tentative)`,
                  )}
                  {DetailsField(
                    "Tenure (In months)",
                    eSignData?.data?.repaymentTime,
                  )}
                  {DetailsField(
                    t("dueOn"),
                    `${eSignData?.data?.dueOn}${getOrdinalSuffix(
                      eSignData?.data?.dueOn,
                    )} ${t("ofEveryMonth")}`,
                  )}
                  {DetailsField(
                    t("firstInstallment"),
                    dayjs(eSignData?.data?.firstInstallmentDate, [
                      "DD-MMMM-YYYY",
                      "DD/MM/YYYY",
                    ]).format("DD/MM/YYYY"),
                  )}
                  {DetailsField(
                    t("interestRate"),
                    `${eSignData?.data?.interestRate} %`,
                  )}

                  {DetailsField(
                    t("bankName"),
                    toTitleCase(eSignData?.data?.bankName),
                  )}
                  {DetailsField(
                    t("transferToAc"),
                    mask(eSignData?.data?.transferToAccount),
                  )}

                  {eSignData?.data?.optionalTotalInsurancePremium > 0 ? (
                    <>
                      {DetailsField(
                        "Total Insurance Premium (18% GST Incl.)",
                        CurrencyConverter(
                          eSignData?.data?.optionalTotalInsurancePremium ?? 0,
                        ),
                      )}
                      <Typography
                        display={"inline"}
                        fontWeight={{ md: 400, xs: 500 }}
                        fontSize={{ xs: "10px", md: "14px" }}
                        color={"#004135"}
                        sx={{
                          cursor: "pointer",
                          textDecoration: "underline",
                          lineHeight: "5px",
                          mt: { xs: -1, md: -3 },
                        }}
                        onClick={() => {
                          sessionStorage.setItem(
                            "insurance_editable",
                            JSON.stringify(false),
                          );
                          router.push(`/${product}/insurance-protection-plan`);
                        }}
                      >
                        Know More
                      </Typography>
                    </>
                  ) : null}
                  <Typography
                    fontWeight={350}
                    fontStyle={"italic"}
                    color={WEB_COLORS.mainColorShade2}
                    sx={{ fontSize: { xs: "12px", md: "16px" } }}
                  >
                    *Annual Percentage Rate is Tentative
                  </Typography>
                </>
              )}
              {!eSignData?.data?.insuranceTaken && (
                <Stack
                  alignItems={"center"}
                  onClick={() => setFullView((view) => !view)}
                >
                  <Box display={"flex"} alignItems={"center"}>
                    {!fullView ? (
                      <>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: "12px", md: "16px" },
                          }}
                        >
                          View More
                        </Typography>
                        <KeyboardArrowDown />
                      </>
                    ) : (
                      <>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: "12px", md: "16px" },
                          }}
                        >
                          View Less
                        </Typography>
                        <KeyboardArrowUp />
                      </>
                    )}
                  </Box>
                </Stack>
              )}
            </Stack>
          </PaperComponent>
        </Stack>
      </PaperComponent>
      {!eSignData?.data?.insuranceTaken &&
        productApplication?.nomineeName === null &&
        !productApplication?.agreementDocumentId &&
        !productApplication?.kfsDocumentId && (
          <InsurancePlan
            offerReminder={offerReminder}
            handleLimitedOfferModal={handleLimitedOfferModal}
            handleContinueWithInsurance={handleProceed}
            handleContinueWithoutInsurance={handleContinueWithoutInsurance}
            refetchESignData={refetchESignData}
            setNomineeDetailsModalVisible={setNomineeDetailsModalVisible}
          />
        )}

      <NomineeFields
        open={nomineeDetailsModalVisible}
        productApplication={productApplication}
        handleModal={handleNominee}
        handleOnProceed={handleNomineeAPiRequest}
      />
      <ModalComponent
        closeButtonRequired={false}
        open={dobMismatchModalVisible}
      >
        <Stack alignItems={"center"} justifyContent={"center"} gap={4}>
          <Typography variant="h6">{creatLegalityData?.data?.label}</Typography>
          <Typography variant="h7">
            {creatLegalityData?.data?.message}
          </Typography>
          <ButtonComponent onClick={handleDobMismatchError}>Ok</ButtonComponent>
        </Stack>
      </ModalComponent>
      <ModalComponent
        closeButtonRequired={false}
        open={isAgreementDocumentModalOpen}
        handleModal={() => setIsAgreementDocumentModalOpen(false)}
      >
        <Stack
          sx={{
            backgroundColor: { md: "#F2F7F799" },
            border: { md: "1px solid #F0F0F099", xs: "none" },
            borderRadius: "20px",
          }}
          spacing={2}
          width={"100%"}
          flexDirection={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          pb={{ md: 10, xs: 2 }}
          pt={{ md: 10, xs: 2 }}
        >
          <Image
            src={IMAGES.authenticationError}
            width={isBelowMd ? 180 : 250}
            alt="Attempts Exhausted"
          />
          <Stack
            // width={{ md: "620px" }}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            spacing={1}
          >
            <Typography
              textAlign={"center"}
              fontWeight={400}
              fontSize={{ md: "16px", xs: "14px" }}
              color={WEB_COLORS.textFadeColor}
              mb={2}
            >
              {legalityStatusData?.data?.message
                ?.split("\n")
                ?.map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
            </Typography>
          </Stack>
          <Stack width={{ sm: "300px", xs: "100%" }}>
            <ButtonComponent
              onClick={() => {
                if (legalityStatusData?.data?.url) {
                  product &&
                    sessionStorage.setItem(APP_KEYS.PRODUCT_TYPE, product);
                  window.location.href = legalityStatusData?.data?.url;
                } else if (legalityStatusData?.data?.stage) {
                  router.replace(
                    STAGE_BASED_ROUTING(legalityStatusData?.data?.stage),
                  );
                }
                setIsAgreementDocumentModalOpen(false);
              }}
            >
              {legalityStatusData?.data?.url ? "Proceed" : "Ok "}
            </ButtonComponent>
          </Stack>
        </Stack>
      </ModalComponent>
    </Stack>
  );
};

export default EsignScreen;
